import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";

import LMap from "components/map/Map";

import { getAssets } from "service/assets";
import { getWellSummary, getAssetCrs } from "service/wellSummary";
import {
  getTotalProductionsData,
  getProductionsData,
} from "service/productionData";
import { getMapFeatures, summarizeCrs } from "utils/map";
import { getDateBefore } from "utils/dateUtil";

import {
  setAssets,
  setActiveAssetId,
  setActiveAssetByName,
  setAssetWells,
  selectAssets,
  selectAssetWells,
  selectActiveAssetId,
  selectActiveAsset,
} from "features/asset/assetSelectorSlice";

import { DataPeriodOptions } from "app/codes";
import SectionSetting from "components/common/SectionSetting";

const ProductOptions = [
  { value: "oil_prd_rate", name: "Cumulative Oil" },
  { value: "gas_prd_rate", name: "Cumulative Gas" },
  { value: "water_prd_rate", name: "Cumulative Water" },
  { value: "avg_oil_gas_prd_rate", name: "Average Oil-gas Ratio" },
  { value: "avg_water_cut", name: "Average Water-cut" },
  { value: "power_consumption", name: "Power Consumption" },
];

const zoomFactor = 15;

// AV-A2: number of days each relative window spans (0 = all-time, no window)
const PeriodDays = { 1: 7, 2: 30, 3: 365 };

// AV-A2: derive last_data_date client-side from the summed time-series
// response ({ product: [[dateStr, value], ...], ... }). Returns the max date
// across all product series, or null when no dated points exist.
function getLastDataDate(seriesData) {
  if (seriesData == null) {
    return null;
  }
  let maxTime = null;
  for (let product of Object.keys(seriesData)) {
    let series = seriesData[product];
    if (!Array.isArray(series)) {
      continue;
    }
    for (let point of series) {
      let dateStr = Array.isArray(point) ? point[0] : point && point.date;
      if (dateStr == null) {
        continue;
      }
      let time = new Date(dateStr).getTime();
      if (!isNaN(time) && (maxTime === null || time > maxTime)) {
        maxTime = time;
      }
    }
  }
  return maxTime === null ? null : new Date(maxTime);
}

function AssetMap(props) {
  const { showWellSelects, assetSelected, bubbleSelected } = props;
  const { loadingIconActions } = props;
  const [showLoadingIcon, hideLoadingIcon] = loadingIconActions || [];

  const assets = useSelector(selectAssets);
  const activeAssetId = useSelector(selectActiveAssetId);
  const activeAsset = useSelector(selectActiveAsset);
  const assetWells = activeAsset ? activeAsset.wells : null;

  const dispatch = useDispatch();

  const [productionsData, setProductionsData] = useState(null);
  const [activeProduct, setActiveProduct] = useState(ProductOptions[0].value);
  const [selectedWellIds, setSelectedWellIds] = useState([]);

  // const [zoomFactor, setZoomFactor] = useState(15);
  const [features, setFeatures] = useState(null);
  const [center, setCenter] = useState([0.0, 46.036]);
  const [bounds, setBounds] = useState(null);
  const [period, setPeriod] = useState(0);
  // AV-C5: coordinate reference system label (datum + UTM zone) for the active
  // asset. "—" until loaded / when unknown (never defaulted to WGS84).
  const [crs, setCrs] = useState({ datumLabel: "—", zoneLabel: "—" });

  // AV-C5: fetch the per-well CRS for the asset and collapse it to a label.
  const loadAssetCrs = useCallback((assetId) => {
    if (assetId == null) {
      setCrs({ datumLabel: "—", zoneLabel: "—" });
      return;
    }
    getAssetCrs(
      assetId,
      (result) => setCrs(summarizeCrs(result)),
      () => setCrs({ datumLabel: "—", zoneLabel: "—" })
    );
  }, []);

  let assetName = null;
  if (activeAsset) {
    assetName = activeAsset.asset_name;
  }

  const loadAssetWells = useCallback(
    (assetId) => {
      showLoadingIcon && showLoadingIcon();
      getWellSummary(
        assetId,
        (result) => {
          let wells = [];
          if (result && result.length > 0) {
            wells = result
              .slice()
              .sort((a, b) => (a.well_name > b.well_name ? 1 : -1));
          }
          dispatch(setAssetWells({ assetId: assetId, wells }));
          hideLoadingIcon && hideLoadingIcon();
        },
        (err) => {
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    },
    [dispatch, hideLoadingIcon, showLoadingIcon]
  );

  const updateProductionsData = useCallback((result, product) => {
    let data = {};
    let wellIds = Object.keys(result);
    if (product === "avg_oil_gas_prd_rate") {
      for (let wellId of wellIds) {
        let productions = result[wellId];
        if (productions === undefined || productions == null) {
          continue;
        }
        let oilRate = productions["oil_prd_rate"];
        let gasRate = productions["gas_prd_rate"];
        if (
          oilRate !== undefined &&
          oilRate != null &&
          gasRate !== undefined &&
          gasRate != null
        ) {
          // AV-A1: do NOT drop oil=0 wells (pure gas wells). When oil=0 the
          // GOR is undefined (gas/0 -> infinite); keep the well visible and
          // flag it as an infinite/special GOR instead of filtering it out.
          if (oilRate !== 0) {
            let gasOilRatio = gasRate / oilRate;
            data[wellId] = { avg_oil_gas_prd_rate: gasOilRatio };
          } else {
            // oil == 0: gas>0 -> infinite GOR (special marker); gas==0 -> 0/0
            // is treated as 0 (no production). Either way the well stays on map.
            data[wellId] = {
              avg_oil_gas_prd_rate: gasRate > 0 ? Infinity : 0,
              avg_oil_gas_prd_rate_infinite: gasRate > 0,
            };
          }
        }
      }
    } else if (product === "avg_water_cut") {
      for (let wellId of wellIds) {
        let productions = result[wellId];
        if (productions === undefined || productions == null) {
          continue;
        }
        let oilRate = productions["oil_prd_rate"];
        let waterRate = productions["water_prd_rate"];
        if (
          oilRate !== undefined &&
          oilRate != null &&
          waterRate !== undefined &&
          waterRate != null
        ) {
          let baseRate = oilRate + waterRate;
          let waterCut = 0;
          if (baseRate !== 0) {
            waterCut = waterRate / baseRate;
            data[wellId] = { avg_water_cut: waterCut };
          }
        }
      }
    } else {
      for (let wellId of wellIds) {
        let productions = result[wellId];
        if (productions === undefined || productions == null) {
          continue;
        }
        let value = productions[product];
        if (value != null && value !== undefined) {
          data[wellId] = {};
          data[wellId][product] = value;
        }
      }
    }
    setProductionsData(data);
  }, []);

  // Fetch the aggregated totals for a [startDate, endDate] window and feed the
  // map. startDate/endDate null => all-time.
  const fetchTotals = useCallback(
    (assetId, product, startDate, endDate) => {
      let products = null;
      if (
        [
          "oil_prd_rate",
          "gas_prd_rate",
          "water_prd_rate",
          "power_consumption",
        ].includes(product)
      ) {
        products = [product];
      } else {
        products = ["oil_prd_rate", "gas_prd_rate", "water_prd_rate"];
      }

      getTotalProductionsData(
        null,
        assetId,
        products,
        startDate,
        endDate,
        (result) => {
          updateProductionsData(result, product);
          hideLoadingIcon && hideLoadingIcon();
        },
        (error) => {
          hideLoadingIcon && hideLoadingIcon();
        }
      );
    },
    [hideLoadingIcon, updateProductionsData]
  );

  const queryProductData = useCallback(
    (assetId, product, period) => {
      if (assetId == null) {
        return;
      }

      showLoadingIcon && showLoadingIcon();

      // All-time window: no date filtering, query directly.
      const days = PeriodDays[period];
      if (!days) {
        fetchTotals(assetId, product, null, null);
        return;
      }

      // AV-A2: relative windows (week/month/year) must anchor to the LAST date
      // that actually has data, not new Date() (today). Production data lags
      // 1-3 months, so anchoring to today makes "last 30 days" land in an empty
      // interval -> the map shows 0 / a false drop. The aggregate /total
      // endpoint returns one value per well (no dates), so we derive
      // last_data_date client-side from the summed time-series endpoint
      // (getProductionsData), then window the totals against it. No new BE
      // endpoint required; if a dedicated last_data_date endpoint is added
      // later, swap this lookup for it.
      getProductionsData(
        null,
        assetId,
        ["oil_prd_rate", "gas_prd_rate", "water_prd_rate"],
        null,
        null,
        true,
        (seriesData) => {
          let lastDataDate = getLastDataDate(seriesData);
          let endDate = lastDataDate || new Date();
          let startDate = getDateBefore(endDate, days);
          fetchTotals(assetId, product, startDate, endDate);
        },
        (error) => {
          // Fall back to anchoring on today if the series lookup fails.
          let endDate = new Date();
          let startDate = getDateBefore(endDate, days);
          fetchTotals(assetId, product, startDate, endDate);
        }
      );
    },
    [showLoadingIcon, fetchTotals]
  );

  useEffect(() => {
    showLoadingIcon && showLoadingIcon();
    getAssets(
      (result) => {
        hideLoadingIcon && hideLoadingIcon();
        if (result == null || result.length === 0) {
          return;
        }
        dispatch(setAssets(result));

        const assetId = result[0].id;
        dispatch(setActiveAssetId(assetId));

        if (showWellSelects === false && assetSelected) {
          assetSelected(assetId);
        }

        loadAssetWells(assetId);
        loadAssetCrs(assetId);
        queryProductData(assetId, ProductOptions[0].value, 0);
      },
      (error) => {
        hideLoadingIcon && hideLoadingIcon();
      }
    );
  }, [
    dispatch,
    hideLoadingIcon,
    showLoadingIcon,
    queryProductData,
    loadAssetWells,
    loadAssetCrs,
    assetSelected,
    showWellSelects,
  ]);

  useEffect(() => {
    if (
      activeAssetId != null &&
      assetWells != null &&
      assetWells.length > 0 &&
      (!selectedWellIds || selectedWellIds.length === 0)
    ) {
      const wellData = assetWells[0];
      if (wellData && bubbleSelected) {
        bubbleSelected(activeAssetId, wellData.uwi);
      }
    }
  }, [activeAssetId, assetWells, selectedWellIds, bubbleSelected]);

  useEffect(() => {
    if (
      assetWells == null ||
      productionsData == null ||
      activeProduct == null
    ) {
      return;
    }

    const [tempFeatures, mapCenter, mapBounds] = getMapFeatures(
      productionsData,
      assetWells,
      activeProduct
    );
    console.log("tempFeatures", tempFeatures);
    console.log("mapCenter", mapCenter);
    console.log("mapBounds", mapBounds);
    // if (!tempFeatures || isNaN(tempFeatures[0]?.Value)) return;
    setFeatures(tempFeatures);
    setCenter(mapCenter);
    setBounds(mapBounds);
  }, [assetWells, productionsData, activeProduct]);

  const handleAssetChanged = useCallback(
    (e) => {
      e.preventDefault();
      const assetId = parseInt(e.target.value);
      dispatch(setActiveAssetId(assetId));

      if (showWellSelects === false && assetSelected) {
        assetSelected(assetId);
      }

      loadAssetWells(assetId);
      loadAssetCrs(assetId);
      setProductionsData(null);
      if (assetId != null && activeProduct != null && period != null) {
        queryProductData(assetId, activeProduct, period);
      }
    },
    [
      activeAssetId,
      activeProduct,
      assetSelected,
      assets,
      dispatch,
      loadAssetWells,
      loadAssetCrs,
      period,
      queryProductData,
      showWellSelects,
    ]
  );

  function handleProductChanged(e) {
    e.preventDefault();
    const product = e.target.value;
    setActiveProduct(product);

    if (activeAssetId != null && product != null && period != null) {
      queryProductData(activeAssetId, product, period);
    }
  }

  function changePeriod(value) {
    if (typeof value === "string") {
      value = parseInt(value);
    }
    setPeriod(value);

    if (activeAssetId != null && activeProduct != null && value != null) {
      queryProductData(activeAssetId, activeProduct, value);
    }
  }

  const onSelectedWellChange = useCallback(
    (wellId) => {
      setSelectedWellIds([wellId]);
      if (bubbleSelected) {
        bubbleSelected(activeAssetId, wellId);
      }
    },
    [activeAssetId, bubbleSelected]
  );

  const handleWellSelected = useCallback(
    (e) => {
      const wellId = e.target.value;
      onSelectedWellChange(wellId);
    },
    [onSelectedWellChange]
  );

  function handleObjectiveSelected(objective) {
    if (showWellSelects) {
      if (!selectedWellIds.includes(objective.apiId)) {
        onSelectedWellChange(objective.apiId);
      }
    }
  }

  const productsSelect = useMemo(() => {
    return ProductOptions.map((item, index) => {
      return (
        <option key={index} value={item.value}>
          {item.name}
        </option>
      );
    });
  }, []);

  const assetsSelect = useMemo(() => {
    if (!assets || assets.length === 0) {
      return <></>;
    }

    return assets.map((asset, index) => {
      return (
        <option key={index} value={asset.id}>
          {asset.asset_name}
        </option>
      );
    });
  }, [assets]);

  const wellsSelect = useMemo(() => {
    if (!showWellSelects || !assetWells || assetWells.length === 0) {
      return null;
    }

    const selects = assetWells.map((item, index) => {
      return (
        <option key={index} value={item.uwi}>
          {item.well_name}
        </option>
      );
    });

    return (
      <>
        <select
          defaultValue={
            selectedWellIds && selectedWellIds.length > 0
              ? selectedWellIds[0]
              : ""
          }
          onChange={handleWellSelected}
        >
          {selects}
        </select>
      </>
    );
  }, [assetWells, selectedWellIds, showWellSelects, handleWellSelected]);

  return (
    <div className="cards-container h-100">
      <div className="cards-header d-flex flex-row justify-content-between align-items-center">
        <span className="header-title">
          Map
          {/* AV-C5: coordinate reference system (datum + UTM zone) so the
              user knows the basis of the well positions (Permian NAD27↔83
              offset ~100m). "—" when unknown; never assumed WGS84. */}
          <span
            className="ms-2"
            style={{ fontSize: "0.75rem", fontWeight: 400, opacity: 0.8 }}
            title="Coordinate reference system of well positions"
          >
            CRS: {crs.datumLabel}
            {crs.zoneLabel && crs.zoneLabel !== "—"
              ? ` · ${crs.zoneLabel}`
              : ""}
          </span>
        </span>
        <div className="header-menu-container d-flex flex-row">
          <select
            className=""
            onChange={handleAssetChanged}
            value={activeAssetId || ""}
          >
            {assetsSelect}
          </select>
          {wellsSelect}
          <select
            className=""
            onChange={handleProductChanged}
            value={activeProduct || ""}
          >
            {productsSelect}
          </select>
          <select
            className=""
            value={period || ""}
            onChange={(e) => {
              changePeriod(e.target.value);
            }}
          >
            {DataPeriodOptions.map((item, index) => {
              return (
                <option key={index} value={index}>
                  {" "}
                  {item.name}{" "}
                </option>
              );
            })}
          </select>
          <div className="cards-setting-btn">
            <SectionSetting disable />
          </div>
        </div>
      </div>
      <div className="cards-content">
        <div className="chart-container">
          <LMap
            objectives={features ? features : []}
            center={center}
            zoom={zoomFactor}
            bounds={bounds}
            selectedObjectiveIds={new Set(selectedWellIds)}
            objectiveSelectedHandler={handleObjectiveSelected}
          />
        </div>
      </div>
    </div>
  );
}

export { AssetMap };
